import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/images";
import { HeroCarousel } from "@/components/HeroCarousel";
import { SectionHeader } from "@/components/Cards";
import { FadeUp, Stagger, item } from "@/components/Reveal";

export default function Home() {
  const { content } = useLang();
  const { home, ui } = content;

  return (
    <>
      <HeroCarousel />

      <section data-testid="home-about-section" className="bg-white">
        <div className="container-x py-24 md:py-36">
          <div className="grid grid-cols-12 gap-8 items-start">
            <FadeUp className="col-span-12 lg:col-span-7">
              <div className="earmark mb-5">{ui.about}</div>
              <h1 data-testid="home-intro-title" className="h-display text-4xl sm:text-5xl lg:text-[3.25rem] text-black">{home.hero.title}</h1>
            </FadeUp>
            <FadeUp delay={0.15} className="col-span-12 lg:col-span-4 lg:col-start-9 lg:pt-9">
              <p data-testid="home-intro-description" className="text-sm md:text-base leading-relaxed text-[var(--ink-2)]">{home.hero.text}</p>
              {home.hero.paragraphs?.map((paragraph, i) => (
                <p key={i} data-testid={`home-intro-paragraph-${i}`} className="mt-6 text-sm md:text-base leading-relaxed text-[var(--ink-2)]">{paragraph}</p>
              ))}
              <Link to="/a-propos" data-testid="home-about-cta" className="btn-dark mt-10">{home.about.cta} <ArrowRight size={16} /></Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="home-services-section" className="bg-[var(--bg-grey)]">
        <div className="container-x py-24 md:py-36">
          <SectionHeader eyebrow={ui.ourServices} title={home.services.heading} text={home.services.text} />
          <Stagger className="mt-10 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:mt-12 lg:grid-cols-4" stagger={0.1}>
            {home.services.items.map((s) => (
              <motion.div key={s.slug} variants={item}>
                <Link to={`/services/${s.slug}`} data-testid={`home-service-card-${s.slug}`} className="group flex h-full flex-col focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black">
                  <img src={IMAGES.services[s.slug]} alt={s.title} data-testid={`home-service-image-${s.slug}`} loading="lazy" className="mb-6 block aspect-[16/10] w-full object-cover" />
                  <h3 data-testid={`home-service-title-${s.slug}`} className="font-head text-xl md:text-2xl font-medium tracking-tight text-black">{s.title}</h3>
                  <p data-testid={`home-service-description-${s.slug}`} className="mt-4 flex-1 text-[13.5px] leading-relaxed text-[var(--ink-2)]">{s.text}</p>
                  <span data-testid={`home-service-discover-${s.slug}`} className="mt-6 self-start text-sm text-black underline decoration-black/40 underline-offset-4 transition-colors duration-300 group-hover:decoration-black">{ui.discoverPrefix}</span>
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <section data-testid="home-insights-section" aria-labelledby="insights-heading" className="bg-white border-t border-[var(--line)]">
        <div className="container-x py-20 md:py-28">
          <h2 id="insights-heading" data-testid="insights-heading" className="h-section text-2xl sm:text-3xl text-black">{ui.pointsOfView}</h2>
          <Stagger className="mt-12 grid grid-cols-1 gap-x-12 gap-y-16 lg:grid-cols-2" stagger={0.1}>
            {(content.insights || []).slice(0, 2).map((ins) => (
              <motion.div key={ins.slug} variants={item}>
                <Link to={`/points-de-vue/${ins.slug}`} data-testid={`home-insight-card-${ins.slug}`} className="group block">
                  <div className="relative overflow-hidden bg-[var(--pov-dark)]">
                    <img src={ins.image} alt={ins.title} data-testid={`home-insight-image-${ins.slug}`} loading="lazy" className="aspect-[16/9] w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-[1.03]" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#232323] via-[#232323]/75 to-transparent" />
                    <div className="absolute inset-0 flex flex-col justify-end p-8 sm:p-12">
                      <h3 data-testid={`home-insight-title-${ins.slug}`} className="font-pov max-w-lg text-2xl md:text-3xl text-white">{ins.title}</h3>
                    </div>
                  </div>
                  <p className="mt-7 font-pov text-lg md:text-xl text-[#1d1d1d]">{ins.subtitle}</p>
                  <p className="mt-5 max-w-2xl text-sm md:text-[15px] leading-relaxed text-[var(--ink-2)]">{ins.excerpt}</p>
                  <span className="mt-7 inline-block text-sm text-black underline decoration-black/40 underline-offset-4 transition-colors duration-300 group-hover:decoration-black">{ui.readInsight}</span>
                </Link>
              </motion.div>
            ))}
          </Stagger>
          <Link to="/points-de-vue" data-testid="home-insights-learn-more" className="btn-dark mt-16">{ui.learnMore} <ArrowRight size={16} /></Link>
        </div>
      </section>

      <section data-testid="home-news-section" aria-labelledby="news-heading" className="bg-[var(--bg-grey)] border-t border-[var(--line)]">
        <div className="container-x py-20 md:py-28">
          <h2 id="news-heading" data-testid="news-heading" className="h-section text-2xl sm:text-3xl text-black">{ui.news}</h2>
          <Stagger className="mt-12 grid grid-cols-1 gap-x-12 gap-y-16 lg:grid-cols-2" stagger={0.1}>
            {(content.news || []).slice(0, 2).map((a) => (
              <motion.div key={a.slug} variants={item}>
                <Link to={`/actualites/${a.slug}`} data-testid={`home-news-card-${a.slug}`} className="group block">
                  <div className="relative overflow-hidden bg-[var(--pov-dark)]">
                    <img src={a.image} alt={a.title} data-testid={`home-news-image-${a.slug}`} loading="lazy" className="aspect-[16/9] w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-[1.03]" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#232323] via-[#232323]/75 to-transparent" />
                    <div className="absolute inset-0 flex flex-col justify-end p-8 sm:p-12">
                      <h3 data-testid={`home-news-title-${a.slug}`} className="font-pov max-w-lg text-2xl md:text-3xl text-white">{a.title}</h3>
                    </div>
                  </div>
                  <p className="mt-7 font-pov text-lg md:text-xl text-[#1d1d1d]">{a.subtitle}</p>
                  <p className="mt-5 max-w-2xl text-sm md:text-[15px] leading-relaxed text-[var(--ink-2)]">{a.excerpt}</p>
                  <span className="mt-7 inline-block text-sm text-black underline decoration-black/40 underline-offset-4 transition-colors duration-300 group-hover:decoration-black">{ui.readNews}</span>
                </Link>
              </motion.div>
            ))}
          </Stagger>
          <Link to="/actualites" data-testid="home-news-learn-more" className="btn-dark mt-16">{ui.learnMore} <ArrowRight size={16} /></Link>
        </div>
      </section>
    </>
  );
}
